import React, { Component, PropTypes } from 'react';
import classNames from 'classnames';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import Swipeable from 'react-swipeable';
import _map from 'lodash/map';
import _debounce from 'lodash/debounce';
import { bylineToString } from 'utils/NewsUtils';
import * as AnalyticsUtils from 'utils/AnalyticsUtils';
import ComponentTitle from '../shared/ComponentTitle';
import Image from 'components/Image';
import Timestamp from 'components/Timestamp';
import Slideshow from 'components/icons/Slideshow';
import Pause from 'components/icons/Pause';
import injectTapEventPlugin from "react-tap-event-plugin";
import { TITLE_TYPE } from '../../constants/FRNBaseStyles';
import ImageStyleUtils from 'utils/ImageStyleUtils';

const WIDGETS = 'widgets';
const IMAGE_GALLERY = 'ImageGallery';

const ImagesPropTypes = {
  showPagination: PropTypes.bool,
  showCounter: PropTypes.bool,
  autoChangeInterval: PropTypes.number,
  autoPause: PropTypes.bool,
  showByline: PropTypes.bool,
  showDescription: PropTypes.bool,
  showTimestamp: PropTypes.bool,
  descriptionBackgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  showTitle: PropTypes.bool,
  enableImageAnalytics: PropTypes.bool,
  displayType: PropTypes.oneOf(['cover', 'contain'])
};

class ImageSlideshowWrapper extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array.isRequired,
    title: PropTypes.string,
    backgroundColor: PropTypes.string,
    numberOfImagesDisplayed: PropTypes.number,
    titleType: PropTypes.string,
    ...ImagesPropTypes
  }

  static defaultProps = {
    showTimestamp: true,
    backgroundColor: '#F3F3F3',
    descriptionBackgroundColor: '#FFFFFF',
    textColor: '#FFFFFF',
    showByline: true,
    showDescription: true,
    numberOfImagesDisplayed: 0,
    showPagination: true,
    showCounter: true,
    autoChangeInterval: 0,
    autoPause: false,
    showTitle: true,
    titleType: TITLE_TYPE.MODULE_TITLE,
    displayType: 'contain'
  }

  constructor(props) {
    super(props);
    if (typeof window !== 'undefined') {
      injectTapEventPlugin({
        shouldRejectClick: () => {
          return true;
        }
      });
    }
  }

  render() {
    if (!this.props.FRN_rawResponses || this.props.FRN_rawResponses.length === 0 || !this.props.FRN_rawResponses[0].data) {
        return null;
    }

    const {
      FRN_rawResponses: [{
        data: {
          id = '',
          contentClassification = '',
          storyimages: storyImages = [],
          features = [],
          headline = '',
          bylines = []
        }
      } = {}] = [],
      title,
      backgroundColor,
      numberOfImagesDisplayed,
      showPagination,
      showCounter,
      autoChangeInterval,
      autoPause,
      showByline,
      showDescription,
      showTimestamp,
      descriptionBackgroundColor,
      textColor,
      showTitle,
      enableImageAnalytics,
      titleType,
      displayType
    } = this.props;

    let images;

    let metaData;

    // The feed contains "ImageGallery".
    const widget = features.find(item => item.type === WIDGETS);
    if (widget) {
      const imagesGallery = widget.json.find(item => item.Type === IMAGE_GALLERY);
      if (imagesGallery) {
        images = imagesGallery.images || [];

        /** If enableImageAnalytics, set to metadata of slideshow to each item */
        if (enableImageAnalytics) {
          const {
            id: widgetId
          } = widget;

          const {
            slideshowHeadline: widgetHeadline,
            adTag
          } = imagesGallery;

          metaData = {
            widgetId,
            widgetHeadline,
            adTag
          };
        }
      }
    }

    // If the feed doesn't contain "ImageGallery", data will be in the "storyimages"
    if (!images) {
      images = storyImages.map(item => {
        return {
          'url': item.filename,
          'description': item.caption,
          'lastEditedDate': item.lastEditedDate
        };
      });

      /** If enableImageAnalytics, set to metadata of slideshow to each item */
      if (enableImageAnalytics) {
        metaData = {
          widgetId: id,
          widgetHeadline: headline,
          adTag: contentClassification
        };
      }
    }

    const componentTitle = title || headline;
    let availableImages = images.slice(0, numberOfImagesDisplayed <= 0 ? images.length : numberOfImagesDisplayed);

    if (enableImageAnalytics) {
      availableImages = availableImages.map(item => {
        return { ...item, ...metaData };
      });
    }

    return (
      <div className="ImageSlideshowWrapper" style={{backgroundColor}}>
        {showTitle && componentTitle ? <ComponentTitle titleType={titleType} title={componentTitle} /> : null}
        <ImageSlideshow
          items={availableImages}
          bylines={bylines}
          autoChangeInterval={autoChangeInterval}
          autoPause={autoPause}
          showPagination={showPagination}
          showCounter={showCounter}
          showTimestamp={showTimestamp}
          showByline={showByline}
          showDescription={showDescription}
          descriptionBackgroundColor={descriptionBackgroundColor}
          textColor={textColor}
          enableImageAnalytics={enableImageAnalytics}
          displayType={displayType}
          />
      </div>
    );
  }
}

class ImageSlideshow extends Component {
  static propTypes = {
    items: PropTypes.array.isRequired,
    bylines: PropTypes.array,
    ...ImagesPropTypes
  }

  state = {
    index: 0,
    type: '',
    paused: false,
  }

  componentWillMount() {
    this._onBack = _debounce(this._onBack, 500, { maxWait: 500});
    this._onNext = _debounce(this._onNext, 500, { maxWait: 500});
  }

  componentDidMount() {
    const { items, enableImageAnalytics, autoChangeInterval } = this.props;

    if (enableImageAnalytics) {
      const firstItemMeta = {
        slideshowData: items[0],
        index: 1,
        numberOfItems: items.length
      };

      AnalyticsUtils.triggerSlideshowPageView(firstItemMeta);

      if (autoChangeInterval) {
        AnalyticsUtils.triggerSlideshowStart(firstItemMeta);
      }
    }

    this._startAutoChangeInterval();
  }

  componentWillUnmount() {
    this._clearAutoChangeInterval();
  }

  _startAutoChangeInterval() {
    const { autoChangeInterval } = this.props;
    if (autoChangeInterval > 0 && !this.state.paused) {
      this.imageSlideshowInterval = setInterval(() => this._onNext(), autoChangeInterval * 1000);
    }
  }

  _clearAutoChangeInterval() {
    const { autoChangeInterval } = this.props;
    if (autoChangeInterval > 0) {
      clearInterval(this.imageSlideshowInterval);
    }
  }

  _onNext(e) {
    this._clearAutoChangeInterval();

    const { items, enableImageAnalytics } = this.props;
    let { index } = this.state;

    index = (index === this.props.items.length - 1 ? 0 : index + 1);

    this.setState({
      index,
      type: 'slideNext'
    });

    if (enableImageAnalytics) {
      const slideshowEventMeta = {
        slideshowData: items[index],
        index: index + 1,
        numberOfItems: items.length
      };

      AnalyticsUtils.triggerSlideshowPageView(slideshowEventMeta);

      if (e) {
        AnalyticsUtils.triggerSlideshowSlideNext(slideshowEventMeta);
      }
    }

    this._startAutoChangeInterval();
  }

  _onBack(e) {
    this._clearAutoChangeInterval();

    const { items, enableImageAnalytics } = this.props;
    let { index } = this.state;

    index = (index === 0 ? this.props.items.length - 1 : index - 1);

    this.setState({
      index,
      type: 'slideBack'
    });

    if (enableImageAnalytics) {
      const slideshowEventMeta = {
        slideshowData: items[index],
        index: index + 1,
        numberOfItems: items.length
      };

      AnalyticsUtils.triggerSlideshowPageView(slideshowEventMeta);

      if (e) {
        AnalyticsUtils.triggerSlideshowSlideBack(slideshowEventMeta);
      }
    }

    this._startAutoChangeInterval();
  }

  _handleSwipe(e, x) {
    if (x > 0) {
      this._onNext(e);
    } else {
      this._onBack(e);
    }
  }

  _handleTouchTap() {
    return this.state.paused ? this._unpause() : this._pause();
  }

  _pause() {
    const {
      autoChangeInterval,
      autoPause,
      enableImageAnalytics,
      items
    } = this.props;

    const { index } = this.state;

    if (autoChangeInterval && autoPause){
      this._clearAutoChangeInterval();
      this.setState({ paused: true });

      if (enableImageAnalytics) {
        AnalyticsUtils.triggerSlideshowPause({slideshowData: items[index], index: index + 1, numberOfItems: items.length });
      }
    }
  }

  _unpause() {
    const {
      autoChangeInterval,
      autoPause,
      enableImageAnalytics,
      items
    } = this.props;

    const { index } = this.state;

    if (autoChangeInterval && autoPause) {
      this.setState({ paused: false });
      setTimeout(() => !this.state.paused ? this._onNext() : null, 1500);

      if (enableImageAnalytics) {
        AnalyticsUtils.triggerSlideshowStart({slideshowData: items[index], index: index + 1, numberOfItems: items.length });
      }
    }
  }

  render() {
    const {
      items,
      bylines,
      showPagination,
      showCounter,
      showDescription,
      showByline,
      showTimestamp,
      descriptionBackgroundColor,
      textColor,
      displayType
    } = this.props;

    const { index, type, paused } = this.state;

    const {
      publishingDate,
      url: imageUrl,
      description = '',
      lastEditedDate = ''
    } = items[index];

    const authors = _map(bylines, bylineToString);

    const timestamp = {
      publishDate: showTimestamp ? publishingDate : null,
      authors: showByline ? authors : null
    };

    const isCoverType = displayType === 'cover';
    const wrapperClassName = classNames('image-wrapper', {'image-cover': isCoverType});
    // if ofjectFit = contain we should center the image
    const imageStyles = ImageStyleUtils(displayType);

    return (
      <div className="ImageSlideshow">
        <div className="ImageSlideshow-thumbnail ImageSlideshow-coverPhoto ImageSlideshow-main"
          onMouseEnter={() => this._pause()}
          onMouseLeave={() => this._unpause()}>
          <ReactCSSTransitionGroup
            transitionName={type}
            transitionEnterTimeout={500}
            transitionLeaveTimeout={500}
            className="slide-container"
            onTouchTap={() => this._handleTouchTap()}>
            <div key={index} className="slide">
              <Swipeable className="swipe" onSwiped={(e, x) => this._handleSwipe(e, x)}>
                <div className={wrapperClassName}>
                  <Image
                    height={isCoverType ? "540" : "480"}
                    src={`${decodeURIComponent(imageUrl)}`}
                    lastEditedDate={lastEditedDate}
                    imageStyles={imageStyles}
                  />
                </div>
              </Swipeable>
            </div>
          </ReactCSSTransitionGroup>
          {showPagination ?
            <div className="ImageSlideshow-paginationControls">
              <div onClick={(e) => this._onBack(e)}>
                <Slideshow direction="left"/>
              </div>
              <div onClick={(e) => this._onNext(e)}>
                <Slideshow direction="right"/>
              </div>
            </div> : null
          }
          {showCounter ?
            <div className="ImageSlideshow-itemCount">
              <p>{index + 1}/{items.length}</p>
            </div> : null
          }
          <div className="ImageSlideshow-pause"><Pause paused={paused} /></div>
        </div>
        <div className="ImageSlideshow-slideshowText ImageSlideshow-cardEdge ImageSlideshow-cardInset"
          style={{backgroundColor: descriptionBackgroundColor}}>
          {showTimestamp ? <Timestamp {...timestamp} /> : null}
          {showDescription ?
            <div className="ImageSlideshow-descriptionText">
              <p style={{color: textColor}} dangerouslySetInnerHTML={{__html: unescape(description) }} />
            </div> : null
          }
        </div>
      </div>
    );
  }
}

export default ImageSlideshowWrapper;



// WEBPACK FOOTER //
// ./app/components/news/ImageSlideshow.js